import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { OnboardingShell } from '../../components/onboarding/OnboardingShell';
import { useAuth } from '../../store/auth';
import { useMe } from '../../hooks/useMe';
import { useFirstName } from '../../hooks/useFirstName';
import { apiFetch } from '../../lib/api-client';
import styles from './AgePrivacy.module.css';

export function AgePrivacy(): JSX.Element {
  const navigate = useNavigate();
  const { t } = useTranslation(['flows', 'common']);
  const jwt = useAuth((s) => s.jwt);
  const { data: me } = useMe();
  const firstName = useFirstName();
  const [age, setAge] = useState(false);
  const [gdpr, setGdpr] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Prefill checkboxes from stored consent timestamps so a returning user
  // doesn't have to re-check both rows after going back.
  useEffect(() => {
    if (!me) return;
    if (me.ageConfirmedAt) setAge(true);
    if (me.gdprConsentAt) setGdpr(true);
  }, [me]);

  const canContinue = age && gdpr && !submitting;

  const handleContinue = async (): Promise<void> => {
    if (!canContinue || !jwt) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await apiFetch('/api/v1/me', jwt, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ageConfirmedAt: true, gdprConsentAt: true, onboardingStep: 2 }),
      });
      if (!res.ok) throw new Error(`patch_failed_${res.status}`);
      navigate('/onboarding/birth', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'unknown_error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <OnboardingShell step={2} etaSeconds={25} backTo="/onboarding/language">
      <header className={styles.header}>
        <span className={styles.tgBadge} aria-label={t('onboarding.agePrivacy.viaTelegram')}>
          <span className={styles.tgAvatar}>{firstName.charAt(0) || '·'}</span>
          {t('onboarding.agePrivacy.viaTelegram')}
        </span>
        <h1 className={styles.title}>
          {t('onboarding.agePrivacy.greetingPrefix')}
          <em className={styles.titleEm} data-testid="age-greeting-firstname">
            {firstName}
          </em>
          {t('onboarding.agePrivacy.greetingSuffix')}
        </h1>
      </header>

      <div className={styles.card}>
        <ConsentRow
          checked={age}
          onChange={setAge}
          title={t('onboarding.agePrivacy.ageGate')}
          subtitle={t('onboarding.agePrivacy.ageGateSub')}
        />
        <ConsentRow
          checked={gdpr}
          onChange={setGdpr}
          title={t('onboarding.agePrivacy.birthConsentTitle')}
          subtitle={t('onboarding.agePrivacy.birthConsentSub')}
        />
      </div>

      <button
        type="button"
        className={styles.policyLink}
        onClick={() => window.open('https://calestea.com/privacy', '_blank', 'noopener')}
      >
        {t('onboarding.agePrivacy.policyLink')} ›
      </button>

      {error && (
        <p className={styles.error} role="alert">
          {error}
        </p>
      )}

      <button
        type="button"
        className={styles.cta}
        disabled={!canContinue}
        aria-disabled={!canContinue}
        onClick={handleContinue}
      >
        {submitting ? t('common:states.saving') : `${t('common:actions.continue')} ✦`}
      </button>
    </OnboardingShell>
  );
}

interface ConsentRowProps {
  checked: boolean;
  onChange: (v: boolean) => void;
  title: string;
  subtitle: string;
}

function ConsentRow({ checked, onChange, title, subtitle }: ConsentRowProps): JSX.Element {
  return (
    <label className={styles.row}>
      <span
        className={`${styles.checkbox} ${checked ? styles.checkboxChecked : ''}`}
        aria-hidden="true"
      >
        {checked && (
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path
              d="M3 7l3 3 5-6"
              stroke="var(--void)"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      <span className={styles.rowText}>
        <span className={styles.rowTitle}>{title}</span>
        <span className={styles.rowSubtitle}>{subtitle}</span>
      </span>
      <input
        type="checkbox"
        className={styles.srOnly}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        aria-label={title}
      />
    </label>
  );
}
